import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../http';
import '../events/EditEvent.css';

/**
 * Admin page: global document library.
 * Documents created here can be assigned to any test event.
 */
export default function DocumentsManager() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState('html');
  const [htmlContent, setHtmlContent] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/documents');
      setDocuments(res.data);
    } catch {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContentType('html');
    setHtmlContent('');
    setPdfFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    if (contentType === 'html' && !htmlContent.trim()) { setError('Content is required'); return; }
    if (contentType === 'pdf' && !pdfFile) { setError('Please select a PDF file'); return; }

    setSaving(true);
    setError('');
    try {
      let payload = { title: title.trim(), content_type: contentType };
      if (contentType === 'html') {
        payload.content = htmlContent;
      } else {
        payload.file_data = await fileToBase64(pdfFile);
        payload.file_name = pdfFile.name;
      }
      await api.post('/api/documents', payload);
      resetForm();
      await loadDocuments();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    setDeleteError('');
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e) {
      setDeleteError(e.response?.data?.error || 'Failed to delete document');
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
        <Link to="/admin" style={{ color: '#6b7280', textDecoration: 'none', fontSize: 14 }}>
          ← Admin
        </Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 6px', fontSize: 24 }}>Document Library</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>
            Manage reusable documents. Assign them to individual test events from the event editor.
          </p>
        </div>
        {!showForm && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            + New Document
          </button>
        )}
      </div>

      {deleteError && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
          {deleteError}
        </div>
      )}

      {showForm && (
        <div className="event-doc-form" style={{ marginBottom: 24 }}>
          <h4>New Document</h4>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              className="form-input"
              type="text"
              placeholder="e.g. Testing Rules & Policies"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Type</label>
            <div className="type-toggle">
              <button type="button" className={`type-btn ${contentType === 'html' ? 'active' : ''}`} onClick={() => setContentType('html')}>
                Text / HTML
              </button>
              <button type="button" className={`type-btn ${contentType === 'pdf' ? 'active' : ''}`} onClick={() => setContentType('pdf')}>
                Upload PDF
              </button>
            </div>
          </div>

          {contentType === 'html' ? (
            <div className="form-group">
              <label className="form-label">Content *</label>
              <textarea
                className="form-input form-textarea"
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="Paste or type the document content. Basic HTML tags are supported (<p>, <strong>, <ul>, etc.)."
                rows={10}
              />
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">PDF File *</label>
              <input ref={fileInputRef} type="file" accept="application/pdf" onChange={(e) => {
                const f = e.target.files[0];
                if (f?.type === 'application/pdf') { setPdfFile(f); setError(''); }
                else { setError('Please select a PDF file'); setPdfFile(null); }
              }} className="form-file-input" />
              {pdfFile && <p className="file-selected-note">Selected: {pdfFile.name} ({(pdfFile.size / 1024).toFixed(0)} KB)</p>}
            </div>
          )}

          {error && <p className="event-docs-error">{error}</p>}

          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Document'}
            </button>
            <button className="btn btn-secondary" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading…</p>
      ) : documents.length === 0 && !showForm ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6b7280' }}>
          <p>No documents yet. Create your first document to get started.</p>
        </div>
      ) : (
        <div className="event-docs-list">
          {documents.map((doc) => (
            <div key={doc.id} className="event-doc-card">
              <div className="event-doc-info">
                <span className="event-doc-name">{doc.title}</span>
                <div className="event-doc-meta">
                  <span className={`doc-type-badge doc-type-${doc.content_type}`}>
                    {doc.content_type.toUpperCase()}
                  </span>
                  {doc.file_name && (
                    <span style={{ fontSize: 12, color: '#9ca3af' }}>{doc.file_name}</span>
                  )}
                </div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(doc)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
